/**
 * Sidebar delete-confirmation hook — bundles the conversation- and module-delete
 * confirm dialogs: the four state atoms, the close/confirm handlers (with their
 * pending-guards), and the two `Escape`-to-close effects.
 *
 * Hook-order safety: the close/confirm functions are PLAIN functions (not
 * memoized), so the only hooks here are four `useState`s and two `useEffect`s.
 * Both effects only attach/detach a `keydown` listener, so their relative
 * execution order vs. other sidebar effects is immaterial.
 */

import { useEffect, useState } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';

interface UseDeleteModalsArgs {
  deleteConversation: (conversationId: string) => Promise<void> | void;
  token: string | null | undefined;
  workspaceId: string;
  queryClient: QueryClient;
}

export function useDeleteModals({
  deleteConversation,
  token,
  workspaceId,
  queryClient,
}: UseDeleteModalsArgs) {
  const [deleteConversationTarget, setDeleteConversationTarget] = useState<{ id: string; name: string } | null>(null);
  const [isDeleteConversationPending, setIsDeleteConversationPending] = useState(false);
  const [deleteModuleTarget, setDeleteModuleTarget] = useState<{ id: string; name: string } | null>(null);
  const [isDeleteModulePending, setIsDeleteModulePending] = useState(false);

  const handleCloseDeleteConversationModal = () => {
    if (isDeleteConversationPending) return;
    setDeleteConversationTarget(null);
  };

  const handleConfirmDeleteConversation = () => {
    if (!deleteConversationTarget || isDeleteConversationPending) return;

    setIsDeleteConversationPending(true);
    void (async () => {
      try {
        await deleteConversation(deleteConversationTarget.id);
        setDeleteConversationTarget(null);
      } catch (error) {
        console.error('[ConversationSidebar] Failed to delete conversation:', error);
      } finally {
        setIsDeleteConversationPending(false);
      }
    })();
  };

  useEffect(() => {
    if (!deleteConversationTarget) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || isDeleteConversationPending) return;
      setDeleteConversationTarget(null);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [deleteConversationTarget, isDeleteConversationPending]);

  const handleCloseDeleteModuleModal = () => {
    if (isDeleteModulePending) return;
    setDeleteModuleTarget(null);
  };

  const handleConfirmDeleteModule = () => {
    if (!deleteModuleTarget || isDeleteModulePending || !token || !workspaceId) return;

    setIsDeleteModulePending(true);
    void (async () => {
      try {
        await api.workspaces.deleteWorkspaceModule(token, workspaceId, deleteModuleTarget.id, { permanent: true });
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) }),
          queryClient.invalidateQueries({ queryKey: queryKeys.modules.sidebarPreferences(workspaceId) }),
          queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all }),
        ]);
        setDeleteModuleTarget(null);
      } catch (error) {
        console.error('[ConversationSidebar] Failed to delete module:', error);
      } finally {
        setIsDeleteModulePending(false);
      }
    })();
  };

  useEffect(() => {
    if (!deleteModuleTarget) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || isDeleteModulePending) return;
      setDeleteModuleTarget(null);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [deleteModuleTarget, isDeleteModulePending]);

  return {
    deleteConversationTarget,
    setDeleteConversationTarget,
    isDeleteConversationPending,
    deleteModuleTarget,
    setDeleteModuleTarget,
    isDeleteModulePending,
    handleCloseDeleteConversationModal,
    handleConfirmDeleteConversation,
    handleCloseDeleteModuleModal,
    handleConfirmDeleteModule,
  };
}
